import { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { fitNameCount, leafName } from "./categoryLabel";

export interface CategoryNamesFitBudgetValue {
  /** Width, in px, the surrounding item may take on its line (the header badge, outer chrome included). */
  budget: number | null;
  /** Receives the item's floor: first name in full + " +N" for the rest + the badge's chrome. */
  onMinimum?: (width: number) => void;
}

/** Absent, the names fill their own container's width (the KPI card). */
export const CategoryNamesFitBudget = createContext<CategoryNamesFitBudgetValue | null>(null);

interface Measures {
  names: number[];
  separator: number;
  /** `suffixes[r]` is the width of " +r". */
  suffixes: number[];
}

// Measures each candidate piece in a throwaway, invisible layer inside `root`, so it inherits the
// same font as the visible text, and removes it before returning. Nothing is left in the document.
function measure(root: HTMLElement, leaves: string[]): Measures {
  const layer = document.createElement("span");
  layer.setAttribute("aria-hidden", "true");
  layer.style.cssText =
    "position:absolute;left:0;top:0;height:0;width:max-content;overflow:hidden;visibility:hidden;pointer-events:none;white-space:pre";
  const piece = (text: string): HTMLElement => {
    const el = document.createElement("span");
    el.setAttribute("data-fit-measure", "");
    el.style.display = "inline-block";
    el.textContent = text;
    layer.appendChild(el);
    return el;
  };
  const names = leaves.map(piece);
  const separator = piece(", ");
  const suffixes = [null, ...leaves.slice(1).map((_, i) => piece(` +${i + 1}`))];
  root.appendChild(layer);
  const width = (el: HTMLElement | null) => (el ? el.getBoundingClientRect().width : 0);
  const result = { names: names.map(width), separator: width(separator), suffixes: suffixes.map(width) };
  root.removeChild(layer);
  return result;
}

const sum = (widths: number[]) => widths.reduce((total, w) => total + w, 0);

interface CategoryNamesFitProps {
  /** Category names or paths, in display order; only the leaf of each is shown. */
  names: string[];
}

/**
 * One line of leaf names: as many as fit the available width, joined with ", ", then " +N" for the
 * rest. If not even the first fits, it is truncated with an ellipsis and the "+N" stays visible.
 */
export function CategoryNamesFit({ names }: CategoryNamesFitProps) {
  const context = useContext(CategoryNamesFitBudget);
  const budget = context ? context.budget : null;
  const onMinimum = context?.onMinimum;
  const leaves = useMemo(() => names.map(leafName), [names]);
  const key = leaves.join("\u0000");
  const rootRef = useRef<HTMLSpanElement>(null);
  const [measures, setMeasures] = useState<Measures | null>(null);
  const [available, setAvailable] = useState<number | null>(null);
  const [chrome, setChrome] = useState(0);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    setMeasures(measure(root, leaves));
    // A web font that finishes loading after this measure changes every width: measure once more.
    let active = true;
    // `document.fonts` is absent in jsdom.
    document.fonts?.ready.then(() => {
      if (active && rootRef.current) setMeasures(measure(rootRef.current, leaves));
    });
    return () => {
      active = false;
    };
    // `key` stands for the contents of `leaves`; the array identity changes with every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const recalc = () => {
      if (budget === null) return setAvailable(root.getBoundingClientRect().width);
      // The badge around the names (icon, padding, chevron) is whatever the pill adds to the names' box.
      const around = root.parentElement ? Math.max(0, root.parentElement.getBoundingClientRect().width - root.getBoundingClientRect().width) : 0;
      setChrome(around);
      setAvailable(Math.max(0, budget - around));
    };
    recalc();
    if (budget !== null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(recalc);
    observer.observe(root);
    return () => observer.disconnect();
  }, [budget, key]);

  const total = leaves.length;

  // The floor the line may squeeze this badge to: the first name in full plus " +N" for the rest.
  useLayoutEffect(() => {
    if (!onMinimum || budget === null || !measures || total === 0) return;
    onMinimum(Math.ceil(measures.names[0] + (total > 1 ? measures.suffixes[total - 1] : 0) + chrome));
  }, [onMinimum, budget, measures, chrome, total]);

  const shown =
    measures && available !== null
      ? fitNameCount({ nameWidths: measures.names, separatorWidth: measures.separator, suffixWidths: measures.suffixes, available })
      : total;
  // In a pill the names' box is as wide as what is shown, capped at what the line leaves.
  // What is actually on the line: the shown names, their separators and the " +N" suffix.
  const shownWidth =
    measures && shown > 0
      ? sum(measures.names.slice(0, shown)) + (shown - 1) * measures.separator + (shown < total ? measures.suffixes[total - shown] : 0)
      : null;
  const width = budget !== null && shownWidth !== null && available !== null ? Math.ceil(Math.min(shownWidth, available)) : undefined;

  return (
    <span
      ref={rootRef}
      data-testid="category-names-fit"
      className="relative flex min-w-0 max-w-full whitespace-nowrap"
      style={width === undefined ? undefined : { width }}
      // In a badge the hover card already lists every name; standing alone, the native title does.
      title={budget === null ? leaves.join(", ") : undefined}
    >
      {/* eslint-disable-next-line signal/no-unwrapped-clipped-text -- <TruncatedText> mounts a tooltip, which cannot nest in the badge's own hover card; the full list is revealed by that card or by the title above */}
      <span className="min-w-0 truncate">{leaves.slice(0, shown).join(", ")}</span>
      {shown < total && <span className="shrink-0 whitespace-pre">{` +${total - shown}`}</span>}
    </span>
  );
}
